import { useState } from 'react'
import { Button } from '../../components'
import {
  useCreateLinkCode,
  useTelegramStatus,
  useUnlinkTelegram,
} from '../../lib/api/settings-telegram'

// Styled with the app's CSS-variable design system (not raw Tailwind grays) so
// the link code stays readable in dark mode — the previous version rendered
// near-white text on a fixed light-gray chip ("white on white").
const codeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 22,
  letterSpacing: '0.18em',
  color: 'var(--fg-1)',
  background: 'var(--bg-3)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  padding: '4px 12px',
}

const inlineCode: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  color: 'var(--fg-1)',
  background: 'var(--bg-3)',
  borderRadius: 4,
  padding: '1px 6px',
}

export function TelegramSection() {
  const { data, isLoading } = useTelegramStatus()
  const [code, setCode] = useState<{
    code: string
    expiresAt: string
    botUsername: string
  } | null>(null)
  const create = useCreateLinkCode()
  const unlink = useUnlinkTelegram()

  if (isLoading) {
    return <section className="card" style={{ padding: 16, color: 'var(--fg-3)' }}>Завантаження…</section>
  }

  return (
    <section
      className="card"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg-1)' }}>Telegram-сповіщення</h2>

      {data?.linked ? (
        <>
          <p style={{ fontSize: 13, color: 'var(--fg-2)' }}>
            Привʼязано: chat_id <code style={inlineCode}>{data.chatIdMasked}</code>
          </p>
          <Button
            variant="secondary"
            size="sm"
            disabled={unlink.isPending}
            onClick={() => unlink.mutate(undefined, { onSuccess: () => setCode(null) })}
            style={{ alignSelf: 'flex-start' }}
          >
            Відвʼязати
          </Button>
        </>
      ) : code ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--fg-2)' }}>Ваш код:</span>
            <code style={codeStyle}>{code.code}</code>
          </div>
          <p style={{ fontSize: 12, color: 'var(--fg-3)' }}>Дійсний 10 хв. У Telegram:</p>
          <ol
            style={{
              fontSize: 13,
              color: 'var(--fg-2)',
              listStyle: 'decimal',
              paddingLeft: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <li>
              Відкрийте{' '}
              <a
                href={`https://t.me/${code.botUsername}?start=${code.code}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--accent)', textDecoration: 'underline' }}
              >
                @{code.botUsername}
              </a>{' '}
              і натисніть «Start» — цього достатньо для привʼязки.
            </li>
            <li>
              Або надішліть боту <code style={inlineCode}>/link {code.code}</code> (чи просто сам
              код).
            </li>
          </ol>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button
              variant="secondary"
              size="sm"
              icon="copy"
              onClick={() => navigator.clipboard.writeText(code.code)}
            >
              Скопіювати код
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCode(null)}>
              Скасувати
            </Button>
          </div>
        </>
      ) : (
        <Button
          variant="primary"
          size="sm"
          disabled={create.isPending}
          onClick={() => create.mutate(undefined, { onSuccess: (d) => setCode(d) })}
          style={{ alignSelf: 'flex-start' }}
        >
          Привʼязати Telegram
        </Button>
      )}
    </section>
  )
}
